import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid2,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx'
import dayjs from 'dayjs'

import PageHeader from '../components/common/PageHeader'
import api from '../services/authService'
import projectService, { Project } from '../services/projectService'
import type { CheckInOutStatus, CheckInOutRecord } from '../types/checkinout'
import CommonPagination from '../components/common/CommonPagination'
import FilterGrid2 from '../components/common/Filters/FilterGrid2'
import SearchButton from '../components/common/Filters/SearchButton'
import RangeDateFilter from '../components/common/Filters/RangeDateFilter'
import SingleSelectFilter from '../components/common/Filters/SingleSelectFilter'
import CustomAutocomplete from '../components/common/Filters/CustomAutocomplete'
import { convertThaiDateToISO } from '../utils/dateUtils'
import CheckInOutDetailDialog from '../components/dialogs/CheckInOutDetailDialogNew'

interface CheckInOutResponse {
  data: CheckInOutRecord[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

// ประเภทข้อมูลสำหรับผู้ใช้
interface User {
  _id: string
  username: string
  name: string
  employeeCode?: string
  phoneNumber?: string
  position?: string
  isEnable: boolean
  objectId?: string
}

interface UsersResponse {
  data: User[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

const processPagination = (
  records: CheckInOutRecord[],
  currentPage: number,
  rowsPerPage: number,
) => {
  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const paginatedRecords = records.slice(startIndex, endIndex)

  return {
    paginatedRecords,
    totalPages: Math.ceil(records.length / rowsPerPage),
    totalItems: records.length,
  }
}

const CheckInOutNewPage = () => {
  const [loading, setLoading] = useState(true)
  const [checkInOutRecords, setCheckInOutRecords] = useState<
    CheckInOutRecord[]
  >([])
  const [selectedProject, setSelectedProject] = useState('')
  const [startDate, setStartDate] = useState(
    dayjs().startOf('month').format('YYYY-MM-DD'),
  )
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [projects, setProjects] = useState<Project[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // สถานะสำหรับ Modal
  const [openModal, setOpenModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<CheckInOutRecord | null>(
    null,
  )
  const [openCheckInImage, setOpenCheckInImage] = useState(false)
  const [openCheckOutImage, setOpenCheckOutImage] = useState(false)
  const [selectedImage, setSelectedImage] = useState('')
  const [exportLoading, setExportLoading] = useState(false)

  const [processedData, setProcessedData] = useState<{
    paginatedRecords: CheckInOutRecord[]
    totalPages: number
    totalItems: number
  }>({
    paginatedRecords: [],
    totalPages: 1,
    totalItems: 0,
  })

  useEffect(() => {
    fetchProjects()
    fetchCheckInOutData()
  }, [page])

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true)
      const { projects: projectList } = await projectService.getProjects({
        page: 1,
        perPage: 100,
      })
      setProjects(projectList)
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }

  const fetchUsers = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setUsers([])
      return
    }

    try {
      setLoadingUsers(true)
      const response = await api.get<UsersResponse>('/users', {
        params: {
          search: searchQuery,
          page: 1,
          perPage: 10,
        },
      })

      setUsers(response.data.data)
    } catch (error) {
      console.error('Error fetching users:', error)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  const fetchCheckInOutData = async () => {
    try {
      setLoading(true)
      const response = await api.get<CheckInOutRecord[]>(
        '/project-checkins/txns',
        {
          params: {
            page,
            perPage: rowsPerPage,
            projectId: selectedProject || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            username: selectedUser?.username || undefined,
          },
        },
      )

      setCheckInOutRecords(response.data)
    } catch (error) {
      console.error('Error fetching check-in/out data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1) // Reset to first page when searching
    fetchCheckInOutData()
  }

  const handleProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProject(event.target.value)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartDate(event.target.value)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
  }

  const handleRangeStartDateChange = (date: string) => {
    setStartDate(convertThaiDateToISO(date))
  }

  const handleRangeEndDateChange = (date: string) => {
    setEndDate(convertThaiDateToISO(date))
  }

  const handleUserInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setSearchTerm(value)
    fetchUsers(value)
  }

  const handleUserChange = (
    _event: React.SyntheticEvent,
    newValue: User | null,
  ) => {
    setSelectedUser(newValue)
  }

  const handleClearFilters = () => {
    setSelectedProject('')
    setStartDate(dayjs().startOf('month').format('YYYY-MM-DD'))
    setEndDate(dayjs().format('YYYY-MM-DD'))
    setSearchTerm('')
    setSelectedUser(null)
  }

  const handleViewDetails = (record: CheckInOutRecord) => {
    setSelectedRecord(record)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedRecord(null)
  }

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(1) // Reset to first page when changing rows per page
  }

  const handleViewImage = (imageUrl: string, isCheckIn: boolean) => {
    setSelectedImage(imageUrl)
    if (isCheckIn) {
      setOpenCheckInImage(true)
    } else {
      setOpenCheckOutImage(true)
    }
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  // Format time for display
  const formatTime = (dateString: string | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateWithTime = (dateString: string | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Format status for display
  const formatStatus = (status: CheckInOutStatus) => {
    switch (status) {
      case 'checkin':
        return 'เข้างาน'
      case 'checkout':
        return 'ออกงาน'
      default:
        return status
    }
  }

  const handleExport = async () => {
    try {
      setExportLoading(true)
      const response = await api.get<CheckInOutResponse>(
        '/project-checkins/all',
        {
          params: {
            projectId: selectedProject || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            username: selectedUser?.username || undefined,
          },
        },
      )

      // Get maximum number of check-in/out times
      const maxCheckIn = 1 // Always 1 for checkin
      const maxCheckOut = Math.max(
        ...response.data.data.map((record) => record.checkout.length),
      )

      const data = response.data.data.map((record) => {
        const baseData: Record<string, string | number> = {
          ลำดับ: response.data.data.indexOf(record) + 1,
          วันที่: formatDate(record.checkin.activeTime),
          ชื่อพนักงาน: record.checkin?.user?.[0]?.name || record.username,
          โครงการ: record.checkin?.project?.name || '-',
        }

        // Add check-in time
        baseData[`เข้างาน`] = record.checkin?.activeTime
          ? formatTime(record.checkin.activeTime)
          : '-'

        // Add check-out times
        if (record.checkout && record.checkout.length > 0) {
          baseData[`ออกงาน (1)`] = record.checkout[0]?.activeTime
            ? formatTime(record.checkout[0].activeTime)
            : '-'
        } else {
          baseData[`ออกงาน (1)`] = '-'
        }

        if (record.checkout && record.checkout.length > 1) {
          baseData[`ออกงาน (2)`] = record.checkout[record.checkout.length - 1]
            ?.activeTime
            ? formatTime(record.checkout[record.checkout.length - 1].activeTime)
            : '-'
        } else {
          baseData[`ออกงาน (2)`] = '-'
        }

        return baseData
      })

      const ws = xlsxUtils.json_to_sheet(data)
      const wb = xlsxUtils.book_new()
      xlsxUtils.book_append_sheet(wb, ws, 'รายงานเข้า-ออกงาน')
      xlsxWriteFile(
        wb,
        `รายงานเข้า-ออกงาน_${new Date().toISOString().split('T')[0]}.xlsx`,
      )
    } catch (error) {
      console.error('Error exporting data:', error)
    } finally {
      setExportLoading(false)
    }
  }

  // Calculate maximum number of check-in/out times
  const getMaxCheckInOutTimes = (records: CheckInOutRecord[]) => {
    const maxCheckIn = 1 // Always 1 for checkin
    const maxCheckOut = Math.max(
      ...records.map((record) => record.checkout.length),
    )
    return { maxCheckIn, maxCheckOut }
  }

  // Update processed data when checkInOutRecords or page changes
  useEffect(() => {
    const result = processPagination(checkInOutRecords, page, rowsPerPage)
    setProcessedData(result)
  }, [checkInOutRecords, page, rowsPerPage])

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: '100%',
      }}
    >
      <PageHeader title="รายการ เข้า-ออก งาน" />
      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <SingleSelectFilter
            filter={{
              label: 'โครงการ',
              value: 'project',
              options: projects.map((project) => ({
                label: project.name,
                value: project.objectId,
              })),
            }}
            value={selectedProject}
            onChange={handleProjectChange}
            disabled={loadingProjects}
            searchable={true}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <CustomAutocomplete
            label="พนักงาน"
            fullWidth
            placeholder="พิมพ์ชื่อหรือรหัสพนักงาน"
            value={selectedUser}
            onChange={handleUserChange}
            onInputChange={handleUserInputChange}
            options={users}
            loading={loadingUsers}
            noOptionsText="ไม่พบข้อมูลพนักงาน"
            loadingText="กำลังค้นหา..."
            getOptionLabel={(option) =>
              `${option.name}${
                option.employeeCode ? ` (${option.employeeCode})` : ''
              }`
            }
            isOptionEqualToValue={(option, value) =>
              option.username === value.username
            }
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <RangeDateFilter
            onStartDateChange={handleRangeStartDateChange}
            onEndDateChange={handleRangeEndDateChange}
          />
        </Grid2>
        <Grid2>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={loading}
          >
            ล้างตัวกรอง
          </Button>
        </Grid2>
        <Grid2>
          <SearchButton onClick={handleSearch} disabled={loading} />
        </Grid2>
      </FilterGrid2>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ลำดับ</TableCell>
                <TableCell align="center">วันที่</TableCell>
                <TableCell>ชื่อพนักงาน</TableCell>
                <TableCell align="center">โครงการเข้างาน</TableCell>
                <TableCell align="center">เวลาเข้างาน</TableCell>
                <TableCell align="center">โครงการออกงาน 1</TableCell>
                <TableCell align="center">เวลาออกงาน 1</TableCell>
                <TableCell align="center">โครงการออกงาน 2</TableCell>
                <TableCell align="center">เวลาออกงาน 2</TableCell>
                <TableCell align="center">รายละเอียด</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : processedData.paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      ไม่พบข้อมูล
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      กรุณาเลือกโครงการและช่วงวันที่ที่ต้องการดูข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                processedData.paginatedRecords.map((record, index) => {
                  // Get the first and last checkout records
                  const firstCheckout =
                    record.checkout && record.checkout.length > 0
                      ? record.checkout[0]
                      : null
                  const lastCheckout =
                    record.checkout && record.checkout.length > 1
                      ? record.checkout[record.checkout.length - 1]
                      : null

                  return (
                    <TableRow key={record._id || index}>
                      <TableCell align="center">
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(record.checkin.activeTime)}
                      </TableCell>
                      <TableCell>
                        {record.checkin?.user?.[0]?.name || record.username}
                      </TableCell>
                      <TableCell align="center">
                        {record.checkin?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {record.checkin?.activeTime
                          ? formatTime(record.checkin.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {firstCheckout?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {firstCheckout?.activeTime
                          ? formatDateWithTime(firstCheckout.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {lastCheckout?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {lastCheckout?.activeTime
                          ? formatDateWithTime(lastCheckout.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRecord(record)
                            setOpenModal(true)
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CommonPagination
        count={processedData.totalPages}
        page={page}
        onChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        totalItems={processedData.totalItems}
      />

      {/* Modal แสดงรายละเอียดการเข้า-ออกงาน */}
      <CheckInOutDetailDialog
        open={openModal}
        onClose={() => {
          setOpenModal(false)
          setSelectedRecord(null)
        }}
        selectedRecord={selectedRecord}
        onImageClick={(image, isCheckIn) => {
          setSelectedImage(image)
          if (isCheckIn) {
            setOpenCheckInImage(true)
          } else {
            setOpenCheckOutImage(true)
          }
        }}
      />

      {/* Dialog แสดงรูปภาพขนาดใหญ่ */}
      <Dialog
        open={openCheckInImage || openCheckOutImage}
        onClose={() => {
          setOpenCheckInImage(false)
          setOpenCheckOutImage(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          <Box
            component="img"
            src={selectedImage}
            alt="Check-in/out"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenCheckInImage(false)
              setOpenCheckOutImage(false)
            }}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CheckInOutNewPage
